import {AfterViewChecked, Component, EventEmitter, forwardRef, Input, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import { FileUploadApiService } from '../../../services/api/file-upload-api.service';

declare const $: any;

@Component({
    selector: 'text-editor-field',
    templateUrl: './text-editor-field.component.html',
    styleUrls: ['./text-editor-field.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextEditorFieldComponent),
            multi: true
        }
    ],
})
export class TextEditorFieldComponent implements ControlValueAccessor, AfterViewChecked {
    @Output() apply: EventEmitter<any> = new EventEmitter<any>();

    @Output() cancel: EventEmitter<any> = new EventEmitter<any>();

    @Input('value') _value: string = null;

    @Input()
    set options(options: any) {
        this._options = Object.assign(this._options, options);
    }

    /**
     * Defines editor state
     * If not set - default static edit mode
     * true/false - initialize edit/read mode with control buttons
     * @param value
     */
    @Input()
    set editMode(value: boolean) {
        this._editMode = !!value;

        if (!this._editMode) {
            delete this._summernoteObj; // force clean object cause it's DOM element doesn't exist anymore
        }
    }

    get editMode(): boolean {
        return this._editMode;
    }

    get simpleMode() {
        return typeof this.editMode !== 'boolean';
    }

    get value(): string {
        return this._value;
    }

    @Input()
    set value(value: string) {
        if (value !== this._value) {
            this._value = value;
            this.onChangeCallback(value);
        }
    }

    /**
     * Saves text state before edit
     */
    private _beforeEditValue: string;

    private _summernoteObj: any;

    private _editMode: boolean;

    /**
     * Default editor options
     * https://summernote.org/deep-dive/#initialization-options
     */
    private _options: any = {
        dialogsInBody: true,
        toolbar: [
            ['style', ['style']],
            ['fontsize', ['fontsize']],
            ['font', ['bold', 'italic', 'underline', 'clear']],
            ['fontname', ['fontname']],
            ['color', ['color']],
            ['para', ['ul', 'ol', 'paragraph']],
            ['table', ['table']],
            ['insert', ['link', 'hr', 'picture']],
            ['help', ['help']]
        ],
        fontNames: ['Roboto', 'Arial', 'open sans', 'Courier New'],
        disableDragAndDrop: true,
        callbacks: {
            onImageUpload: this.onImageUpload.bind(this)
        }
    };

    constructor(
        private uploader: FileUploadApiService
    ) {}

    ngAfterViewChecked(): void {
        if (this.editMode || this.simpleMode) {
            this.initSummernote(); // init after DOM updated
        }
    }

    writeValue(value: string) {
        if (value !== this._value) {
            this.value = value;

            // update editor content on NgModel changes from outside
            if (this._summernoteObj) {
                this._summernoteObj.summernote('code', value);
            }
        }
    }

    onEdit() {
        this._beforeEditValue = this._value;
        this.editMode = true;
    }

    onApply() {
        this.apply.emit(this);
        this.editMode = false;
    }

    onCancel() {
        this.value = this._beforeEditValue; // force reset value
        this.editMode = false;
    }

    /**
     * Initialize editor
     *
     * @param elementId
     */
    initSummernote(elementId = 'summernote') {
        if (this._summernoteObj) {
            return;
        }

        this._summernoteObj = $(`#${elementId}`);
        this._summernoteObj.summernote(this._options);
        this._summernoteObj.summernote('code', this.value);
        this._summernoteObj.on('summernote.change', (event, contents) => this.value = contents);
    }

    onTouchedCallback: () => void = () => {};

    onChangeCallback: (_: any) => void = () => {};

    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
    }

    /**
     * Uploads image to server
     * and inserts image url to editor
     *
     * @param files
     */
    onImageUpload(files: FileList) {
        this.uploader.fileUpload(files[0]).subscribe(
            result => this._summernoteObj.summernote('insertImage', result.link, result.name)
        );
    }
}



// WEBPACK FOOTER //
// /app/src/app/components/fields/text-editor-field/text-editor-field.component.ts